import React, { useState, useEffect, useCallback } from 'react';
import './NetworkDiscovery.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const NetworkDiscovery = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [locationFilter, setLocationFilter] = useState('');
  const [editingDevice, setEditingDevice] = useState(null);
  const [editForm, setEditForm] = useState({ device_type: '', friendly_name: '' });
  const [investigateResults, setInvestigateResults] = useState(null);
  const [investigating, setInvestigating] = useState(false);
  const [authToken, setAuthToken] = useState(() => sessionStorage.getItem('network_discovery_token'));
  const [pin, setPin] = useState('');

  const fetchDevices = useCallback(async () => {
    if (!authToken) return;
    try {
      setLoading(true);
      let url = `${API_BASE}/api/iot/network-devices?`;
      if (filter) url += `status=${filter}&`;
      if (locationFilter) url += `location=${locationFilter}&`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!response.ok) throw new Error('Failed to fetch devices');
      const data = await response.json();
      setDevices(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter, locationFilter, authToken]);

  useEffect(() => {
    if (authToken) {
      fetchDevices();
    }
  }, [authToken, fetchDevices]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE}/api/iot/auth-network-discovery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin }),
      });
      const data = await response.json();
      if (data.success) {
        sessionStorage.setItem('network_discovery_token', data.token);
        setAuthToken(data.token);
        setPin('');
      } else {
        setError(data.error || 'Invalid PIN');
      }
    } catch (err) {
      setError('Login failed');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('network_discovery_token');
    setAuthToken(null);
  };

  const handleApprove = async (device) => {
    setEditingDevice(device);
    setEditForm({
      device_type: device.device_type || guessDeviceType(device),
      friendly_name: device.friendly_name || device.hostname || ''
    });
  };

  const guessDeviceType = (device) => {
    const vendor = (device.vendor || '').toLowerCase();
    const hostname = (device.hostname || '').toLowerCase();
    if (vendor.includes('tuya') || vendor.includes('aixiangji') || hostname.includes('ty_')) return 'tuya';
    if (vendor.includes('smartthings') || hostname.includes('hub')) return 'smartthings';
    if (vendor.includes('aqara') || vendor.includes('lumi') || hostname.includes('camera')) return 'camera';
    if (vendor.includes('samsung')) return 'smartthings';
    return 'other';
  };

  const submitApprove = async () => {
    try {
      await fetch(`${API_BASE}/api/iot/network-devices/${editingDevice.id}/approve`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}` 
        },
        body: JSON.stringify(editForm),
      });
      setEditingDevice(null);
      fetchDevices();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleIgnore = async (id) => {
    try {
      await fetch(`${API_BASE}/api/iot/network-devices/${id}/ignore`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      fetchDevices();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this device from the list?')) {
      try {
        await fetch(`${API_BASE}/api/iot/network-devices/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${authToken}` },
        });
        fetchDevices();
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleInvestigate = async (device) => {
    setInvestigating(true);
    setInvestigateResults(null);
    try {
      const response = await fetch(`${API_BASE}/api/iot/network-devices/investigation/${encodeURIComponent(device.mac)}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await response.json();
      if (!response.ok) {
        setInvestigateResults({ error: data.error || 'No investigation data found', device });
      } else {
        setInvestigateResults({ ...data, device });
      }
    } catch (err) {
      setInvestigateResults({ error: err.message });
    } finally {
      setInvestigating(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: '#ffc107',
      approved: '#28a745',
      ignored: '#6c757d'
    };
    return (
      <span className="status-badge" style={{ backgroundColor: colors[status] || '#6c757d' }}>
        {status}
      </span>
    );
  };

  if (!authToken) {
    return (
      <div className="network-discovery">
        <div className="login-container">
          <h1>Network Discovery</h1>
          <form onSubmit={handleLogin} className="login-form">
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Enter PIN"
              autoFocus
            />
            <button type="submit">Login</button>
          </form>
          {error && <div className="error">{error}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="network-discovery">
      <div className="header">
        <h1>Network Discovery</h1>
        <button className="logout-btn" onClick={handleLogout}>Logout</button>
      </div>

      <div className="filters">
        <label>
          Status:
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="ignored">Ignored</option>
          </select>
        </label>
        <label>
          Location:
          <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
            <option value="">All</option>
            <option value="thepoint">ThePoint</option>
            <option value="rumsey">Rumsey</option>
          </select>
        </label>
        <button onClick={fetchDevices} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="devices-table-container">
        <table className="devices-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>IP</th>
              <th>MAC</th>
              <th>Hostname</th>
              <th>Vendor</th>
              <th>Location</th>
              <th>First Seen</th>
              <th>Last Seen</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {devices.length === 0 ? (
              <tr><td colSpan="9" className="no-data">No devices found</td></tr>
            ) : (
              devices.map((device) => (
                <tr key={device.id} className={`status-${device.status}`}>
                  <td>{getStatusBadge(device.status)}</td>
                  <td>{device.ip}</td>
                  <td className="mac">{device.mac}</td>
                  <td>{device.friendly_name || device.hostname || '-'}</td>
                  <td>{device.vendor || '-'}</td>
                  <td>{device.location}</td>
                  <td>{formatDate(device.first_seen)}</td>
                  <td>{formatDate(device.last_seen)}</td>
                  <td className="actions">
                    <button className="investigate-btn" onClick={() => handleInvestigate(device)} disabled={investigating}>
                      {investigating ? '...' : '🔍'}
                    </button>
                    {device.status === 'pending' && (
                      <>
                        <button className="approve-btn" onClick={() => handleApprove(device)}>Approve</button>
                        <button className="ignore-btn" onClick={() => handleIgnore(device.id)}>Ignore</button>
                      </>
                    )}
                    {device.status !== 'pending' && (
                      <button className="delete-btn" onClick={() => handleDelete(device.id)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editingDevice && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Approve Device</h2>
            <p><strong>MAC:</strong> {editingDevice.mac}</p>
            <p><strong>IP:</strong> {editingDevice.ip}</p>
            <p><strong>Vendor:</strong> {editingDevice.vendor}</p>
            
            <label>
              Device Type:
              <select 
                value={editForm.device_type} 
                onChange={(e) => setEditForm({...editForm, device_type: e.target.value})}
              >
                <option value="tuya">Tuya</option>
                <option value="smartthings">SmartThings</option>
                <option value="camera">Camera</option>
                <option value="sensor">Sensor</option>
                <option value="other">Other</option>
              </select>
            </label>
            
            <label>
              Friendly Name:
              <input 
                type="text" 
                value={editForm.friendly_name}
                onChange={(e) => setEditForm({...editForm, friendly_name: e.target.value})}
                placeholder="e.g., Living Room Switch"
              />
            </label>
            
            <div className="modal-actions">
              <button className="approve-btn" onClick={submitApprove}>Save</button>
              <button className="cancel-btn" onClick={() => setEditingDevice(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {investigateResults && (
        <div className="modal-overlay">
          <div className="modal investigate-modal">
            <h2>Device Investigation</h2>
            {investigateResults.error ? (
              <p className="error">{investigateResults.error}</p>
            ) : (
              <div className="investigate-results">
                <div className="result-row"><strong>Investigated:</strong> {formatDate(investigateResults.investigated_at)}</div>
                <div className="result-row"><strong>IP:</strong> {investigateResults.ip}</div>
                <div className="result-row"><strong>Online:</strong> {investigateResults.online ? 'Yes' : 'No (may block ping)'}</div>
                <div className="result-row"><strong>MAC:</strong> {investigateResults.mac || 'Unknown'}</div>
                <div className="result-row"><strong>Vendor:</strong> {investigateResults.best_vendor || investigateResults.vendor_online || 'Unknown'}</div>
                
                <div className="result-row">
                  <strong>Hostname:</strong> {investigateResults.best_hostname || 'None'}
                  {(investigateResults.netbios_name || investigateResults.mdns_name) && (
                    <span className="hostname-detail">
                      {investigateResults.netbios_name && ` (NetBIOS: ${investigateResults.netbios_name})`}
                      {investigateResults.mdns_name && ` (mDNS: ${investigateResults.mdns_name})`}
                    </span>
                  )}
                </div>
                
                <div className="result-row">
                  <strong>Open Ports:</strong>
                  {investigateResults.open_ports?.length > 0 ? (
                    <ul className="ports-list">
                      {investigateResults.open_ports.map(p => (
                        <li key={p.port}>
                          {p.port}/tcp ({p.service})
                          {investigateResults.service_details?.[p.port] && (
                            <span className="service-detail"> - {investigateResults.service_details[p.port]}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : ' None found'}
                </div>
                
                {investigateResults.http_info && (
                  <div className="result-row">
                    <strong>HTTP Info:</strong>
                    <ul className="ports-list">
                      {investigateResults.http_info.title && <li>Title: {investigateResults.http_info.title}</li>}
                      {investigateResults.http_info.server && <li>Server: {investigateResults.http_info.server}</li>}
                      {investigateResults.http_info.detected_keyword && <li>Detected: {investigateResults.http_info.detected_keyword}</li>}
                    </ul>
                  </div>
                )}
                
                {investigateResults.snmp_info && (
                  <div className="result-row">
                    <strong>SNMP Info:</strong> {investigateResults.snmp_info.description || investigateResults.snmp_info.name}
                  </div>
                )}
                
                {investigateResults.upnp_info && (
                  <div className="result-row">
                    <strong>UPnP:</strong> {investigateResults.upnp_info.friendly_name} 
                    {investigateResults.upnp_info.model && ` (${investigateResults.upnp_info.model})`}
                  </div>
                )}
                
                {investigateResults.ssh_banner && (
                  <div className="result-row"><strong>SSH:</strong> {investigateResults.ssh_banner}</div>
                )}
                
                {investigateResults.os_guess && (
                  <div className="result-row"><strong>OS Guess:</strong> {investigateResults.os_guess}</div>
                )}
                
                <div className="result-row device-guess">
                  <strong>Device Type:</strong> {investigateResults.device_guess}
                </div>
              </div>
            )}
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setInvestigateResults(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NetworkDiscovery;
